/**
 * Panneau "Jalons" pour une intervention utilisant le nouveau referentiel de
 * jalonnement Contact/Large (SFD v3, #116-#117) : schema du poste + pastilles
 * + une ligne par jalon (ou carte unique "Poste complet" pour les
 * configurations *_LIBRE a un seul jalon). Remplace CompactZoneGrid pour ces
 * interventions ; CompactZoneGrid reste utilise tel quel pour les
 * interventions anterieures (6 zones historiques, `configuration` null).
 *
 * Le bouton photo par jalon de la maquette est volontairement absent ici :
 * differe au Lot 4 (#121-123, entite Photo par jalon + bon PDF).
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { zoneService } from '@/services/zoneService';
import { PosteSchema } from '@/components/zones/PosteSchema';
import type { ConfigurationJalon, DeneignementZone } from '@/types';

interface JalonnementPanelProps {
  deneignementId: number;
  configuration: ConfigurationJalon;
  zones: DeneignementZone[];
  onZoneUpdate?: () => void;
  readOnly?: boolean;
}

function formatHeure(value?: string): string {
  if (!value) return '--:--';
  return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Delai de confirmation legere sur "Fin" (#120) : aucune confirmation sur
 * Debut, mais Fin declenche un compte a rebours annulable avant l'appel API
 * reel, pour rattraper un tap accidentel avec des gants.
 */
const DELAI_CONFIRMATION_FIN_MS = 5000;

const ROW_STYLES = {
  termine: { row: 'border-green-200 bg-green-50', num: 'bg-green-600 text-white', act: 'bg-green-100 text-green-800', label: 'Fait' },
  en_cours: { row: 'border-blue-300 bg-blue-50 ring-2 ring-blue-200', num: 'bg-blue-600 text-white', act: 'bg-blue-600 text-white', label: 'Fin' },
  a_faire: { row: 'border-gray-200 bg-white', num: 'bg-gray-200 text-gray-700', act: 'bg-orange-600 text-white', label: 'Debut' },
} as const;

export function JalonnementPanel({
  deneignementId,
  configuration,
  zones,
  onZoneUpdate,
  readOnly = false,
}: JalonnementPanelProps) {
  const queryClient = useQueryClient();

  const demarrerMutation = useMutation({
    mutationFn: (zoneId: number) => zoneService.demarrerZone(deneignementId, zoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      onZoneUpdate?.();
    },
  });

  const terminerMutation = useMutation({
    mutationFn: (zoneId: number) => zoneService.terminerZone(deneignementId, zoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      onZoneUpdate?.();
    },
  });

  const isPending = demarrerMutation.isPending || terminerMutation.isPending;

  // Confirmation legere sur "Fin" (#120) : stocke le timestamp de debut du
  // compte a rebours par DeneignementZone.id, et le timeout qui declenche
  // l'appel API reel a l'issue du delai (annulable entre-temps).
  const [pendingFinStarts, setPendingFinStarts] = useState<Record<number, number>>({});
  const [, forceTick] = useState(0);
  const pendingFinTimeouts = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (Object.keys(pendingFinStarts).length === 0) return;
    const interval = setInterval(() => forceTick((t) => t + 1), 250);
    return () => clearInterval(interval);
  }, [pendingFinStarts]);

  useEffect(() => {
    const timeouts = pendingFinTimeouts.current;
    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);

  const secondesRestantes = (rowId: number): number => {
    const debut = pendingFinStarts[rowId];
    if (debut === undefined) return 0;
    return Math.max(0, Math.ceil((DELAI_CONFIRMATION_FIN_MS - (Date.now() - debut)) / 1000));
  };

  const annulerFin = (rowId: number) => {
    const timeout = pendingFinTimeouts.current[rowId];
    if (timeout) clearTimeout(timeout);
    delete pendingFinTimeouts.current[rowId];
    setPendingFinStarts((prev) => {
      const { [rowId]: _removed, ...reste } = prev;
      return reste;
    });
  };

  const demarrerFin = (zone: DeneignementZone) => {
    setPendingFinStarts((prev) => ({ ...prev, [zone.id]: Date.now() }));
    pendingFinTimeouts.current[zone.id] = setTimeout(() => {
      delete pendingFinTimeouts.current[zone.id];
      setPendingFinStarts((prev) => {
        const { [zone.id]: _removed, ...reste } = prev;
        return reste;
      });
      terminerMutation.mutate(zone.zone.id);
    }, DELAI_CONFIRMATION_FIN_MS);
  };

  const handleAction = (zone: DeneignementZone) => {
    if (readOnly || isPending) return;
    if (zone.statut === 'a_faire') {
      demarrerMutation.mutate(zone.zone.id);
    } else if (zone.statut === 'en_cours' && pendingFinStarts[zone.id] === undefined) {
      demarrerFin(zone);
    }
  };

  const total = zones.length;
  const termines = zones.filter((z) => z.statut === 'termine').length;
  const enCours = zones.filter((z) => z.statut === 'en_cours').length;
  const aFaire = total - termines - enCours;
  const progression = total > 0 ? (termines / total) * 100 : 0;

  const pins = zones.map((z) => ({
    numero: z.zone.numero,
    statut: z.statut,
    pinX: z.zone.pinX ?? 0.5,
    pinY: z.zone.pinY ?? 0.5,
  }));

  const posteComplet = configuration === 'contact_libre' || configuration === 'large_libre';
  const jalonUnique = posteComplet ? zones[0] : null;

  return (
    <div className="h-full flex flex-col gap-3 overflow-y-auto">
      {/* Barre de progression + comptes */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-600 transition-all duration-300"
            style={{ width: `${progression}%` }}
          />
        </div>
        <span className="font-extrabold text-orange-700 text-sm whitespace-nowrap">
          {termines} / {total} jalon{total > 1 ? 's' : ''}
        </span>
      </div>
      {!posteComplet && (
        <div className="flex justify-center gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-600" />
            {termines} termine{termines > 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-600" />
            {enCours} en cours
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            {aFaire} a traiter
          </span>
        </div>
      )}

      {/* Schema du poste */}
      <PosteSchema configuration={configuration} pins={pins} />

      {/* Poste complet (configurations *_LIBRE, un seul jalon) */}
      {posteComplet && jalonUnique && (
        <div
          className={clsx(
            'rounded-2xl p-4 flex flex-col gap-3.5 border',
            jalonUnique.statut === 'en_cours'
              ? 'bg-blue-50 border-blue-300'
              : jalonUnique.statut === 'termine'
                ? 'bg-green-50 border-green-200'
                : 'bg-white border-gray-200'
          )}
        >
          <div className="flex items-center gap-3">
            <div
              className={clsx(
                'w-10 h-10 rounded-lg flex items-center justify-center font-extrabold text-white',
                jalonUnique.statut === 'termine' ? 'bg-green-600' : 'bg-blue-600'
              )}
            >
              1
            </div>
            <div>
              <div className="font-extrabold">Poste complet</div>
              <div className="text-sm text-gray-600">Traitement global du poste</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-white rounded-lg px-3 py-2.5 flex flex-col gap-0.5">
              <span className="text-xs text-gray-600">Debut</span>
              <span className="text-xl font-extrabold text-green-700 tabular-nums">
                {formatHeure(jalonUnique.heureDebut)}
              </span>
            </div>
            <div className="bg-white rounded-lg px-3 py-2.5 flex flex-col gap-0.5">
              <span className="text-xs text-gray-600">Fin</span>
              <span className="text-xl font-extrabold text-gray-400 tabular-nums">
                {formatHeure(jalonUnique.heureFin)}
              </span>
            </div>
          </div>
          {!readOnly && jalonUnique.statut !== 'termine' && (
            pendingFinStarts[jalonUnique.id] !== undefined ? (
              <button
                onClick={() => annulerFin(jalonUnique.id)}
                className="h-14 rounded-xl bg-gray-700 text-white font-bold text-base"
              >
                Annuler ({secondesRestantes(jalonUnique.id)}s)
              </button>
            ) : (
              <button
                onClick={() => handleAction(jalonUnique)}
                disabled={isPending}
                className="h-14 rounded-xl bg-blue-600 text-white font-bold text-base disabled:opacity-50"
              >
                {jalonUnique.statut === 'a_faire' ? 'Debut de traitement' : 'Fin de traitement'}
              </button>
            )
          )}
        </div>
      )}

      {/* Une ligne par jalon (configurations *_OCCUPE) */}
      {!posteComplet && (
        <div className="flex flex-col gap-2.5">
          {zones.map((z) => {
            const style = ROW_STYLES[z.statut];
            return (
              <div
                key={z.id}
                className={clsx('flex items-center gap-3 p-3 rounded-xl border', style.row)}
              >
                <div
                  className={clsx(
                    'w-9 h-9 rounded-lg flex items-center justify-center font-extrabold flex-shrink-0',
                    style.num
                  )}
                >
                  {z.zone.numero}
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <div className="text-sm font-bold leading-tight">{z.zone.libelle ?? z.zone.nom}</div>
                  <div className="flex gap-3.5 text-xs text-gray-600">
                    <span>
                      Debut <span className="font-semibold text-gray-900 tabular-nums">{formatHeure(z.heureDebut)}</span>
                    </span>
                    <span>
                      Fin <span className="font-semibold text-gray-900 tabular-nums">{formatHeure(z.heureFin)}</span>
                    </span>
                  </div>
                </div>
                {!readOnly && z.statut !== 'termine' && (
                  pendingFinStarts[z.id] !== undefined ? (
                    <button
                      onClick={() => annulerFin(z.id)}
                      className="min-w-[72px] h-11 rounded-lg font-semibold text-sm bg-gray-700 text-white"
                    >
                      Annuler ({secondesRestantes(z.id)}s)
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAction(z)}
                      disabled={isPending}
                      className={clsx('min-w-[72px] h-11 rounded-lg font-semibold text-sm disabled:opacity-50', style.act)}
                    >
                      {style.label}
                    </button>
                  )
                )}
                {z.statut === 'termine' && (
                  <span className={clsx('min-w-[72px] h-11 rounded-lg font-semibold text-sm flex items-center justify-center', style.act)}>
                    Fait
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default JalonnementPanel;
